import styled from 'styled-components';
import {
  colorState,
  device,
  radius,
  spacing,
  typography
} from '../../Universal/Foundation';
import { primaryNavWidth } from '../globalStyles';

export interface SideNavProps {
  isSideNav?: boolean;
}

export interface LegendDropdownProps {
  isDropdownHeight?: boolean;
}

const legendDropdownWidth = '320';

export const GridView = styled.div`
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 16px;

    h1 {
      color: ${colorState.text.primary};
      font-weight: ${typography.weight.semibold};
      font-size: ${typography.size.t1}px;
      margin: 0;
      cursor: default;
    }
  }
`;
export const MapWrapper = styled.div`
  display: flex;
  gap: 16px;
  position: relative;
  flex-wrap: wrap;
  margin-top: 16px;
`;

export const LegendBox = styled.div`
  width: 20px;
  height: 8px;
`;

export const LegendButtonWrapper = styled.div<{ isMapFullyReady: boolean }>`
  opacity: ${({ isMapFullyReady }) => (isMapFullyReady ? 1 : 0)};
  visibility: ${({ isMapFullyReady }) =>
    isMapFullyReady ? 'visible' : 'hidden'};
  transition: 'opacity 0.3s ease-in-out';
`;

export const LegendDropdownMenuWrapper = styled.section<LegendDropdownProps>`
  background-color: white;
  position: absolute;
  right: 0.5%;
  top: 40px;
  border-radius: ${radius.radius_16}px;
  box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.06),
    0px 4px 6px -1px rgba(0, 0, 0, 0.1);
  gap: ${spacing.spacing_4}px;
  width: ${legendDropdownWidth}px;
  height: auto;
  padding: ${spacing.spacing_8}px ${spacing.spacing_0};

  .dropdown-menu-header {
    display: flex;
    justify-content: space-between;
    padding: ${spacing.spacing_8}px 20px;
    align-items: center;
    gap: ${spacing.spacing_4}px;
    font-size: ${typography.size.tm};
    font-weight: ${typography.weight.semibold};
    cursor: default;
  }

  .close-icon {
    cursor: pointer;
    padding: 2px;
    &:hover {
      background-color: ${colorState.surface.brand.hover};
      border-radius: 6px;
    }
  }

  .dropdown-body-section {
    padding: 0 12px;
    gap: ${spacing.spacing_16}px;
    display: flex;
    flex-direction: column;
    max-height: ${(props) => (props.isDropdownHeight ? '150px' : '350px')};
    overflow-y: auto;

    @media ${device.laptopXL} {
      max-height: 200px;
    }

    h3 {
      font-size: ${typography.size.ts};
      font-weight: ${typography.weight.semibold};
      padding-left: ${spacing.spacing_8}px;
      padding-right: ${spacing.spacing_8}px;
      cursor: default;
    }

    ul {
      padding-left: 0;

      li {
        display: flex;
        gap: ${spacing.spacing_12}px;
        align-items: center;
        padding: ${spacing.spacing_8}px;
        margin-bottom: 0.2rem;

        .legend-label {
          font-size: ${typography.size.ts};
          font-weight: ${typography.weight.medium};
        }

        &:hover {
          background-color: ${colorState.surface.brand.tertiary};
          border-radius: ${radius.radius_8}px;
          color: ${colorState.text.brand.secondary};
        }

        &:focus-visible {
          outline: none;
          border-radius: 8px;
          border: solid 1px ${colorState.border.brand.primary};
        }
      }
      .selected-list {
        background-color: ${colorState.surface.brand.tertiary};
        border-radius: ${radius.radius_8}px;
      }
    }
  }
`;

export const CollapseIconContainer = styled.div<SideNavProps>`
  .collapse-icon {
    position: absolute;
    top: ${(props: any) => (props.isSideNav ? '20px' : '16px')};
    left: ${(props: any) =>
      props.isSideNav ? `${primaryNavWidth - 30}px` : '0'};
    z-index: 1;
    background: white;
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
    padding: 0;
    width: 28px;
    height: 28px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    &:hover {
      background-color: ${(props) =>
        !props.isSideNav && colorState.surface.brand.hover};
      svg path {
        stroke: ${colorState.icon.default.primary};
      }
    }

    &:focus-visible {
      border-radius: 8px;
      border: solid 1px ${colorState.border.brand.primary};
      outline: none;
    }

    @media ${device.laptopL} {
      left: ${(props: any) =>
        props.isSideNav ? `${primaryNavWidth - 100}px` : '0'};
    }

    @media ${device.mobileL} {
      left: ${(props: any) => (props.isSideNav ? `0` : '0')};
      display: none;
    }
  }

  &:hover {
    .collapse-left-icon {
      border-radius: 6.86px;
    }

    .collapse-right-icon,
    .collapse-left-icon {
      background-color: ${colorState.surface.brand.hover};
      transition: all 0.3s ease;
    }
  }
`;

export const ClosureMarker = styled.div`
  div[title='animated-marker'] {
    transition: all 0.25s ease;
  }
`;

export const StatusTooltip = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;

  .form-label {
    font-weight: ${typography.weight.medium};
    font-size: ${typography.size.tm};
    color: ${colorState.text.secondary};
  }

  .asterik {
    color: ${colorState.text.danger.primary};
  }

  .tooltip-content {
    display: flex;
    flex-direction: column;
    padding: 12px;
    gap: 12px;

    .content {
      display: flex;
      flex-direction: column;
      gap: 4px;

      .content-heading {
        font-weight: ${typography.weight.semibold};
      }

      .content-description {
        font-weight: ${typography.weight.regular};
      }
    }
  }
`;

export const CustomTextArea = styled.div`
  label {
    font-weight: ${typography.weight.medium};
    font-size: ${typography.size.ts};
    color: ${colorState.text.secondary};

    span {
      white-space: nowrap;
      font-size: ${typography.size.ts};
    }
  }
`;

export const DisapprovalComments = styled.div``;

export const ExtendDurationModal = styled.div`
  font-size: ${typography.size.ts};
  font-weight: ${typography.weight.regular};
  h2 {
    font-size: ${typography.size.ts};
    font-weight: ${typography.weight.semibold};
  }
`;

export const Wrapper = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${colorState.border.default.secondary};
  border-radius: ${radius.radius_8}px;
  background: ${colorState.surface.default.primary};
  position: relative;
  height: 40px;
  min-width: 160px;
  margin: ${spacing.spacing_4}px;

  &:hover {
    border: 1.5px solid ${colorState.border.brand.primary};
  }

  &:focus-within {
    border: 1.5px solid ${colorState.border.brand.primary};
    box-shadow: 0 0 0 3px ${colorState.shadow.brand.primary};
  }

  input::-ms-reveal,
  input::-ms-clear {
    display: none;
  }
`;

export const Prefix = styled.div`
  display: flex;
  align-items: center;
  padding: ${spacing.spacing_8}px ${spacing.spacing_12}px;
  font-size: ${typography.size.tm}px;
  font-weight: ${typography.weight.medium};
`;

export const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 ${spacing.spacing_12}px;
  border-left: 1px solid ${colorState.border.default.secondary};
  gap: ${spacing.spacing_8}px;
  height: 100%;
`;

export const Input = styled.input`
  appearance: none;
  box-sizing: border-box;
  outline: none;
  color: ${colorState.text.primary};
  font-size: ${typography.size.tm};
  font-weight: ${typography.weight.medium};
  border-radius: inherit;
  border: none;
  flex: 1;
  width: 100%;

  &:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 3em white inset;
  }

  ::placeholder {
    color: ${colorState.text.tertiary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &[type='search']::-webkit-search-cancel-button {
    -webkit-appearance: none;
    display: none;
  }
`;

export const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${colorState.text.tertiary};
  width: 24px;
  height: 24px;
  padding: 0;
`;

export const CollapseAndExpandButton = styled.div`
  border-radius: 6px;
  &:hover {
    background-color: ${colorState.surface.brand.hover};
  }
`;
